import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, BarChart, Bar,
} from "recharts";
import { formatRupiah } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { TrendingUp, PieChart as PieIcon, CalendarRange } from "lucide-react";
import { SupplierName } from "@/components/SupplierName";

export const shortRp = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e9) return `${(v / 1e9).toFixed(1).replace(".", ",")}M`;
  if (v >= 1e6) return `${Math.round(v / 1e6)}jt`;
  if (v >= 1e3) return `${Math.round(v / 1e3)}rb`;
  return `${v}`;
};

export const PIE_COLORS = ["#0F172A", "#6366F1", "#22C55E", "#F59E0B", "#0EA5E9", "#CBD5E1"];
const RANGES = [{ v: 1, l: "30 hari" }, { v: 3, l: "3 bln" }, { v: 6, l: "6 bln" }, { v: 12, l: "12 bln" }];

function TipBox({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl bg-slate-900 text-white px-3 py-2 text-xs shadow-xl">
      <div className="font-semibold mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="capitalize text-slate-300">{p.name}</span>
          <span className="font-mono ml-auto">{formatRupiah(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

export function WidgetHead({ icon: Icon, title, sub, children }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
      <div className="min-w-0">
        <div className="text-[17px] font-semibold text-zinc-900 tracking-tight">{title}</div>
        {sub && <div className="text-xs text-zinc-400 mt-0.5">{sub}</div>}
      </div>
      {children}
    </div>
  );
}

export function TrendChart({ data, months, onMonths }) {
  const totalBuy = data.reduce((a, b) => a + b.pembelian, 0);
  const totalPay = data.reduce((a, b) => a + b.pembayaran, 0);
  return (
    <Card className="p-6 h-full dash-rise" data-testid="trend-chart-card">
      <WidgetHead icon={TrendingUp} title="Tren Pembelian vs Pembayaran" sub="Nilai nota masuk dibanding dana yang sudah cair">
        <div className="flex rounded-full bg-slate-100 p-1" data-testid="trend-range-toggle">
          {RANGES.map((r) => (
            <button key={r.v} onClick={() => onMonths(r.v)} data-testid={`trend-range-${r.v}`}
              className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${months === r.v ? "bg-slate-900 text-white shadow" : "text-slate-500 hover:text-slate-900"}`}>
              {r.l}
            </button>
          ))}
        </div>
      </WidgetHead>
      <div className="flex gap-6 mb-2 text-sm">
        <div><div className="flex items-center gap-1.5 text-xs text-slate-500"><span className="h-2 w-2 rounded-full bg-indigo-500" />Pembelian</div><div className="font-mono font-bold text-slate-900" data-testid="trend-total-purchase">{formatRupiah(totalBuy)}</div></div>
        <div><div className="flex items-center gap-1.5 text-xs text-slate-500"><span className="h-2 w-2 rounded-full bg-emerald-500" />Pembayaran</div><div className="font-mono font-bold text-slate-900" data-testid="trend-total-paid">{formatRupiah(totalPay)}</div></div>
      </div>
      <div className="h-[240px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 8, left: -8, bottom: 0 }}>
            <defs>
              <linearGradient id="gBuy" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#6366F1" stopOpacity={0.25} /><stop offset="100%" stopColor="#6366F1" stopOpacity={0} /></linearGradient>
              <linearGradient id="gPay" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#22C55E" stopOpacity={0.2} /><stop offset="100%" stopColor="#22C55E" stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="4 6" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#94A3B8" }} />
            <YAxis tickFormatter={shortRp} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#94A3B8" }} width={48} />
            <Tooltip content={<TipBox />} />
            <Area type="monotone" dataKey="pembelian" name="pembelian" stroke="#6366F1" strokeWidth={2.5} fill="url(#gBuy)" dot={{ r: 3, fill: "#6366F1" }} />
            <Area type="monotone" dataKey="pembayaran" name="pembayaran" stroke="#22C55E" strokeWidth={2.5} fill="url(#gPay)" dot={{ r: 3, fill: "#22C55E" }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

export function DebtPie({ data }) {
  const total = data.reduce((a, b) => a + b.value, 0);
  return (
    <Card className="p-6 h-full dash-rise" data-testid="debt-pie-card">
      <WidgetHead icon={PieIcon} title="Hutang per Supplier" sub="Sisa nota belum lunas" />
      {data.length === 0 ? <div className="text-sm text-slate-400 py-10 text-center" data-testid="debt-pie-empty">Tidak ada hutang berjalan.</div> : (
        <>
          <div className="relative h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" innerRadius={58} outerRadius={82} paddingAngle={3} cornerRadius={6} stroke="none">
                  {data.map((d, i) => <Cell key={d.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => formatRupiah(v)} contentStyle={{ borderRadius: 12, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Total</div>
              <div className="font-mono font-bold text-slate-900" data-testid="debt-pie-total">{shortRp(total)}</div>
            </div>
          </div>
          <div className="mt-3 space-y-2">
            {data.map((d, i) => (
              <div key={d.name} className="flex items-center gap-2 text-sm" data-testid={`debt-pie-row-${i}`}>
                <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                <span className="text-slate-700 truncate"><SupplierName name={d.name} /></span>
                <span className="ml-auto text-xs text-slate-400">{total ? Math.round((d.value / total) * 100) : 0}%</span>
                <span className="font-mono text-xs font-semibold text-slate-900 w-20 text-right">{shortRp(d.value)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}

export function UpcomingBars({ data, overdue }) {
  const total = data.reduce((a, b) => a + b.amount, 0);
  const count = data.reduce((a, b) => a + b.count, 0);
  return (
    <Card className="p-6 h-full dash-rise" data-testid="upcoming-bars-card">
      <WidgetHead icon={CalendarRange} title="Jadwal BG 8 Minggu" sub={`${count} pembayaran · ${formatRupiah(total)}`}>
        {overdue > 0 && <span className="pill-badge bg-rose-50 text-rose-700 border-rose-200 text-xs font-semibold px-3 py-1 rounded-full border" data-testid="upcoming-overdue">{overdue} lewat tempo</span>}
      </WidgetHead>
      <div className="h-[200px] relative">
        {count === 0 && <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none"><span className="rounded-full bg-white/90 border border-slate-200 px-4 py-1.5 text-xs text-slate-500 shadow-sm" data-testid="upcoming-empty">Tidak ada BG/transfer jatuh tempo 8 minggu ke depan</span></div>}
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 4, left: -12, bottom: 0 }}>
            <CartesianGrid strokeDasharray="4 6" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: "#94A3B8" }} interval="preserveStartEnd" minTickGap={8} />
            <YAxis tickFormatter={shortRp} tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: "#94A3B8" }} width={44} />
            <Tooltip cursor={{ fill: "#F1F5F9", radius: 8 }} formatter={(v) => formatRupiah(v)} labelFormatter={(l) => `Minggu ${l}`} contentStyle={{ borderRadius: 12, fontSize: 12 }} />
            <Bar dataKey="amount" name="Jatuh tempo" radius={[8, 8, 8, 8]} maxBarSize={28}>
              {data.map((d, i) => <Cell key={d.label} fill={i === 0 ? "#0F172A" : d.amount > 0 ? "#6366F1" : "#E2E8F0"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
